import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WEBApi } from '../API/WEBApi';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import ImageSelector from '../Components/ImageSelector';

interface Recognition {
  _id: string;
  title: string;
  content: string;
  image?: string;
  date: string;
  published: boolean;
  url: string;
  showURL: boolean;
  orderNo: number;
}

const EditRecognition: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [date, setDate] = useState('');
  const [published, setPublished] = useState(false);
  const [showURL, setShowURL] = useState(false);
  const [url, setUrl] = useState('');
  const [orderNo, setOrderNo] = useState<number>(0);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    WEBApi.getRecognitionById(id)
      .then((recognition: Recognition) => {
        setTitle(recognition.title);
        setContent(recognition.content);
        setImage(recognition.image || '');
        setDate(recognition.date);
        setPublished(recognition.published);
        setShowURL(recognition.showURL);
        setUrl(recognition.url);
        setOrderNo(recognition.orderNo ?? 0);
      })
      .catch(() => setError('Failed to load recognition data.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) {
      setError('Invalid recognition ID.');
      return;
    }
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      await WEBApi.updateRecognition(id, {
        title,
        content,
        image,
        date,
        published,
        url,
        showURL,
        orderNo,
      });
      setMessage('Recognition updated successfully!');
      setTimeout(() => navigate('/breathe-ev-admin/recognitions'), 1500);
    } catch {
      setError('Failed to update recognition. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <h3>Edit Recognition</h3>
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Title */}
        <div className="mb-3">
          <label htmlFor="title" className="form-label">Title</label>
          <input
            id="title"
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        {/* URL */}
        <div className="mb-3">
          <label htmlFor="url" className="form-label">URL</label>
          <input
            id="url"
            type="text"
            className="form-control"
            value={url}
            onChange={e => setUrl(e.target.value)}
            disabled={loading}
            placeholder="Enter unique URL slug (e.g. 'recognition-title')"
          />
        </div>

        <div className="mb-3 form-check">
          <input
            id="showURL"
            type="checkbox"
            className="form-check-input"
            checked={showURL}
            onChange={e => setShowURL(e.target.checked)}
            disabled={loading}
          />
          <label htmlFor="showURL" className="form-check-label">Show URL</label>
        </div>

        {/* Content */}
        <div className="mb-3">
          <label htmlFor="content" className="form-label">Content</label>
          <RichTextEditor value={content} onChange={setContent} />
        </div>

        {/* Image */}
        <div className="mb-3">
          <label className="form-label">Select Image</label>
          <ImageSelector
            selectedUrl={image}
            onSelect={(url) => setImage(url)}
          />
        </div>

        {/* Date */}
        <div className="mb-3">
          <label htmlFor="date" className="form-label">Date</label>
          <input
            id="date"
            type="text"
            className="form-control"
            value={date}
            onChange={e => setDate(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Order Number */}
        <div className="mb-3">
          <label htmlFor="orderNo" className="form-label">Order Number</label>
          <input
            id="orderNo"
            type="number"
            className="form-control"
            value={orderNo}
            onChange={e => setOrderNo(parseInt(e.target.value, 10) || 0)}
            min={0}
            disabled={loading}
          />
        </div>

        {/* Published */}
        <div className="mb-3 form-check">
          <input
            id="published"
            type="checkbox"
            className="form-check-input"
            checked={published}
            onChange={e => setPublished(e.target.checked)}
            disabled={loading}
          />
          <label htmlFor="published" className="form-check-label">Published</label>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Updating...' : 'Update Recognition'}
        </button>
      </form>
    </div>
  );
};

export default EditRecognition;
